import { USER_ROLE } from '../../../constants';

// ─── טיפוסים ───────────────────────────────────────────────────────────────

export type MdxModule = () => Promise<{ default: React.ComponentType; frontmatter: unknown }>;

export interface HelpEntry {
  helpKey: string;
  roles: number[];
  title_he: string;
  title_en?: string;
  route?: string;
  contentModule: {
    he: MdxModule;
    en?: MdxModule;
  };
}

export interface GuideEntry {
  role: number;
  contentModule: {
    he: MdxModule;
    en?: MdxModule;
  };
}

// ─── קיצורים ───────────────────────────────────────────────────────────────

const ALL   = [USER_ROLE.SUPER_ADMIN, USER_ROLE.COMPANY_ADMIN, USER_ROLE.AGENT, USER_ROLE.EMPLOYEE];
const STAFF = [USER_ROLE.COMPANY_ADMIN, USER_ROLE.AGENT, USER_ROLE.EMPLOYEE];
const ADMIN = [USER_ROLE.SUPER_ADMIN, USER_ROLE.COMPANY_ADMIN];
const MGMT  = [USER_ROLE.SUPER_ADMIN, USER_ROLE.COMPANY_ADMIN, USER_ROLE.AGENT];

// ─── Registry מסכי עזרה (Level 2) ──────────────────────────────────────────

export const helpRegistry: HelpEntry[] = [

  // ── ראשי ──────────────────────────────────────────────────────────────────
  {
    helpKey: 'dashboard',
    roles: ALL,
    title_he: 'לוח בקרה',
    title_en: 'Dashboard',
    route: '/dashboard',
    contentModule: { he: () => import('../content/screens/he/dashboard.mdx') },
  },

  // ── בנק ───────────────────────────────────────────────────────────────────
  {
    helpKey: 'bank-list',
    roles: ADMIN,
    title_he: 'חשבונות בנק',
    route: '/bank/list',
    contentModule: { he: () => import('../content/screens/he/bank-list.mdx') },
  },

  // ── מיקומים ───────────────────────────────────────────────────────────────
  {
    helpKey: 'locations-list',
    roles: MGMT,
    title_he: 'רשימת מיקומים',
    route: '/locations/list',
    contentModule: { he: () => import('../content/screens/he/locations-list.mdx') },
  },
  {
    helpKey: 'locations-efficiency',
    roles: MGMT,
    title_he: 'יעילות מסופים',
    title_en: 'Terminal Efficiency',
    route: '/locations/efficiency',
    contentModule: { he: () => import('../content/screens/he/locations-efficiency.mdx') },
  },

  // ── עובדים ────────────────────────────────────────────────────────────────
  {
    helpKey: 'employee-list',
    roles: ADMIN,
    title_he: 'רשימת עובדים',
    route: '/employee/list',
    contentModule: { he: () => import('../content/screens/he/employee-list.mdx') },
  },

  // ── פעילות עובד ───────────────────────────────────────────────────────────
  {
    helpKey: 'employee-activity-charging-terminal',
    roles: STAFF,
    title_he: 'טעינת מסוף',
    route: '/employee-activity/charging-terminal',
    contentModule: { he: () => import('../content/screens/he/employee-activity-charging-terminal.mdx') },
  },
  {
    helpKey: 'employee-activity-bank-withdrawal',
    roles: STAFF,
    title_he: 'משיכה מבנק',
    route: '/employee-activity/bank-withdrawal',
    contentModule: { he: () => import('../content/screens/he/employee-activity-bank-withdrawal.mdx') },
  },

  // ── סוכן ──────────────────────────────────────────────────────────────────
  {
    helpKey: 'agent-list',
    roles: ADMIN,
    title_he: 'רשימת סוכנים',
    route: '/agent/list',
    contentModule: { he: () => import('../content/screens/he/agent-list.mdx') },
  },
  {
    helpKey: 'agent-activity-bank-withdrawal',
    roles: [USER_ROLE.AGENT, USER_ROLE.COMPANY_ADMIN, USER_ROLE.SUPER_ADMIN],
    title_he: 'משיכת בנק — סוכן',
    route: '/agent-activity/bank-withdrawal',
    contentModule: { he: () => import('../content/screens/he/agent-activity-bank-withdrawal.mdx') },
  },

  // ── רווחיות ───────────────────────────────────────────────────────────────
  {
    helpKey: 'profitability-monthly-report',
    roles: ADMIN,
    title_he: 'דוח רווחיות חודשי',
    route: '/profitability/monthly-report',
    contentModule: { he: () => import('../content/screens/he/profitability-monthly-report.mdx') },
  },
  {
    helpKey: 'profitability-expenses',
    roles: ADMIN,
    title_he: 'הוצאות חודשיות',
    route: '/profitability/expenses',
    contentModule: { he: () => import('../content/screens/he/profitability-expenses.mdx') },
  },

  // ── דוחות ─────────────────────────────────────────────────────────────────
  {
    helpKey: 'report-loading-gap',
    roles: ADMIN,
    title_he: 'דוח פערי טעינה',
    route: '/report/loading-gap',
    contentModule: { he: () => import('../content/screens/he/report-loading-gap.mdx') },
  },

  // ── ניהול משתמשים ─────────────────────────────────────────────────────────
  {
    helpKey: 'account-users-list',
    roles: ADMIN,
    title_he: 'ניהול משתמשים',
    route: '/account/users/list',
    contentModule: { he: () => import('../content/screens/he/account-users-list.mdx') },
  },
];

// ─── Registry מדריכי תפקיד (Level 1) ───────────────────────────────────────

export const guideRegistry: GuideEntry[] = [
  {
    role: USER_ROLE.EMPLOYEE,
    contentModule: { he: () => import('../content/guides/he/employee.mdx') },
  },
  {
    role: USER_ROLE.AGENT,
    contentModule: { he: () => import('../content/guides/he/agent.mdx') },
  },
  {
    role: USER_ROLE.COMPANY_ADMIN,
    contentModule: { he: () => import('../content/guides/he/company-admin.mdx') },
  },
  {
    role: USER_ROLE.SUPER_ADMIN,
    contentModule: { he: () => import('../content/guides/he/super-admin.mdx') },
  },
];

// ─── פונקציות עזר ──────────────────────────────────────────────────────────

export function findHelpEntry(helpKey: string): HelpEntry | undefined {
  return helpRegistry.find((e) => e.helpKey === helpKey);
}

export function findGuideEntry(roleId: number): GuideEntry | undefined {
  return guideRegistry.find((e) => e.role === roleId);
}

export function getHelpKeysForRole(roleId: number): string[] {
  return helpRegistry
    .filter((e) => e.roles.includes(roleId))
    .map((e) => e.helpKey);
}
